import { useMemo } from "react";
import { cn } from "@/lib/utils";
import { danceStyleColor } from "@/lib/constants";
import { useDanceStyles } from "@/hooks/useDanceStyles";
import { formatTime, type CalendarDay } from "@/lib/dateUtils";
import { groupEventsByDate } from "@/lib/eventsByDate";
import type { Event } from "./EventCard";
import { DanceStartLine } from "./DanceStartLine";

/**
 * The narrowest a day column may be squeezed before the view scrolls sideways.
 *
 * Sized so the three-column weekend fits a 375px phone outright while the
 * seven-column week overflows and scrolls, which is the intent — a week is
 * meant to be swiped through on a phone and read whole on a desktop.
 */
const MIN_COLUMN_PX = 108;

/**
 * The floor on the grid's height, for a short window or a zoomed-in browser.
 *
 * Above this the grid takes whatever the viewport gives it rather than
 * shrink-wrapping its events — a quiet week should still read as a week, not
 * as a strip of headers with the footer pushed up underneath it.
 */
const MIN_BODY_PX = 320;

interface CalendarDayColumnsProps {
  days: CalendarDay[];
  events: Event[];
  onEventClick: (event: Event) => void;
}

/**
 * The week and weekend views: one column per day, each an ordered list of
 * what is on that night.
 *
 * Deliberately not a time grid. A time grid spends its horizontal space on
 * overlap — every simultaneous event halves the width of its neighbours — and
 * a dance calendar overlaps constantly, with six or more socials all starting
 * at seven. The axis was buying very little in return, since almost everything
 * runs the same evening hours. A list ordered by start time says the same
 * thing and stays readable however many events a night holds.
 */
export function CalendarDayColumns({ days, events, onEventClick }: CalendarDayColumnsProps) {
  const eventsByDate = useMemo(() => groupEventsByDate(events), [events]);

  const total = useMemo(
    () => days.reduce((sum, day) => sum + (eventsByDate.get(day.dateString)?.length ?? 0), 0),
    [days, eventsByDate]
  );

  return (
    <div className="bg-card rounded-xl border border-border/50 overflow-hidden">
      {/* The grid claims the viewport rather than its content. The reserve
          covers what sits above it — site header, count line, calendar header —
          which measures about the same on a phone as on a desktop, plus a
          little breathing room so the card stops short of the fold. */}
      <div
        className="overflow-auto overscroll-contain h-[calc(100dvh-14.5rem)]"
        style={{ minHeight: MIN_BODY_PX }}
      >
        <div
          className="flex flex-col min-h-full"
          style={{ minWidth: days.length * MIN_COLUMN_PX }}
        >
          {/* Day headers — stuck to the top so the date stays visible while
              scrolling a long night. */}
          <div className="flex sticky top-0 z-20 bg-card border-b border-border/50">
            {days.map((day) => (
              <DayColumnHeader key={day.dateString} day={day} />
            ))}
          </div>

          <div className="relative flex flex-1">
            {days.map((day) => (
              <DayColumnBody
                key={day.dateString}
                day={day}
                events={eventsByDate.get(day.dateString) ?? []}
                onEventClick={onEventClick}
              />
            ))}

            {total === 0 && (
              <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                <p className="text-sm text-muted-foreground">
                  Nothing scheduled {days.length === 3 ? "this weekend" : "this week"}
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function DayColumnHeader({ day }: { day: CalendarDay }) {
  return (
    <div className="flex-1 min-w-0 py-2 border-l border-border/50 first:border-l-0">
      <div className="flex flex-col items-center gap-0.5">
        <span
          className={cn(
            "text-[10px] font-medium uppercase tracking-wider",
            day.isToday ? "text-primary" : "text-muted-foreground"
          )}
        >
          {day.date.toLocaleDateString("en-US", { weekday: "short" })}
        </span>
        <span
          className={cn(
            "flex items-center justify-center w-8 h-8 rounded-full text-lg font-display tabular-nums",
            day.isToday ? "bg-primary text-primary-foreground" : "text-foreground"
          )}
        >
          {day.date.getDate()}
        </span>
      </div>
    </div>
  );
}

interface DayColumnBodyProps {
  day: CalendarDay;
  events: Event[];
  onEventClick: (event: Event) => void;
}

function DayColumnBody({ day, events, onEventClick }: DayColumnBodyProps) {
  return (
    <div
      className={cn(
        "flex-1 min-w-0 flex flex-col gap-1 p-1 border-l border-border/50 first:border-l-0",
        day.isToday && "bg-primary/[0.04]"
      )}
    >
      {events.map((event) => (
        <DayColumnEvent key={event._id} event={event} onClick={() => onEventClick(event)} />
      ))}
    </div>
  );
}

/**
 * One night in a day column.
 *
 * Time first, then the name, then where — the order someone scans in when they
 * already know the day and are deciding where to go.
 */
function DayColumnEvent({ event, onClick }: { event: Event; onClick: () => void }) {
  const { styleMap, orderStyles } = useDanceStyles();
  const color = danceStyleColor(orderStyles(event.danceStyles, event.mainStyles), styleMap);

  return (
    <button
      type="button"
      onClick={onClick}
      title={`${formatTime(event.startTime)} · ${event.title}`}
      className={cn(
        "flex w-full flex-col items-start gap-px rounded-md border-l-[3px] px-1.5 py-1 text-left",
        "transition-shadow duration-200 hover:shadow-md",
        "outline-none focus-visible:ring-2 focus-visible:ring-primary/60"
      )}
      style={{ borderLeftColor: color, backgroundColor: `${color}24` }}
    >
      <span className="w-full truncate text-[10px] text-muted-foreground tabular-nums">
        {formatTime(event.startTime)}
      </span>
      <span className="w-full text-[11px] font-semibold leading-tight text-foreground line-clamp-2">
        {event.title}
      </span>
      <DanceStartLine event={event} className="w-full" />
      {event.venueName && (
        <span className="w-full truncate text-[10px] text-muted-foreground">
          {event.venueName}
        </span>
      )}
    </button>
  );
}
